import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Globe,
  XCircle,
  Zap,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { fetchFromUrl } from 'sprites-types/workflow/tools/fetchFromUrl'
import { InstagramPostItem } from '../Instagram/InstagramFetchPostsDisplay'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function CollapsibleHtmlSection({ html }: { html: string }) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="mb-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-2 text-xs font-medium text-gray-700 transition-colors hover:text-gray-900"
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 flex-shrink-0" />
        )}
        <span>HTML Source {isExpanded ? '(hide)' : '(show)'}</span>
        <span className="text-[10px] text-gray-500">({(html.length / 1024).toFixed(1)} KB)</span>
      </button>
      {isExpanded && (
        <div className="mt-2 rounded-2xl bg-gray-50 p-3">
          <pre className="max-h-96 overflow-x-auto overflow-y-auto whitespace-pre-wrap break-words text-[10px] text-gray-800">
            {html}
          </pre>
        </div>
      )}
    </div>
  )
}

function FetchFromUrlDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(fetchFromUrl, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const url = inputs.url || ''
  const responses = Array.isArray(result) ? result : [result]
  const response = responses[0]
  const success = response.success !== false && !response.error
  const error = response.error || ''
  const method = response.method || 'web'
  const title = response.metadata?.title || ''
  const description = response.metadata?.description || ''
  const author = response.metadata?.author || ''
  const platform = response.metadata?.platform || ''
  const statusCode = response.status || 0
  const instagramPost = method === 'instagram' && response.rawData ? response.rawData : undefined

  const contextActions: ContextAction[] = [
    {
      id: 'open-original',
      label: 'Open Original URL',
      icon: <ExternalLink className="h-4 w-4" />,
      onClick: () => {
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer')
        }
      },
      disabled: !url,
    },
    {
      id: 'copy-json',
      label: 'Copy Raw Response',
      icon: <Copy className="h-4 w-4" />,
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(JSON.stringify(responses, null, 2))
        } catch (err) {
          console.error('Failed to copy Smart Fetch result:', err)
        }
      },
    },
  ]

  const getDomain = (urlStr: string) => {
    try {
      return new URL(urlStr).hostname
    } catch {
      return urlStr
    }
  }

  const getStatusColor = (code: number) => {
    if (code >= 200 && code < 300) return 'text-green-600'
    if (code >= 300 && code < 400) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getMethodBadge = (methodType: string) => {
    const methodColors: Record<string, string> = {
      twitter: 'bg-blue-100 text-blue-700',
      instagram: 'bg-pink-100 text-pink-700',
      youtube: 'bg-red-100 text-red-700',
      reddit: 'bg-orange-100 text-orange-700',
      linkedin: 'bg-blue-100 text-blue-700',
      wordpress: 'bg-purple-100 text-purple-700',
      web: 'bg-gray-100 text-gray-700',
    }
    return methodColors[methodType] || methodColors.web
  }

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="px-4 py-3">
          <div className="flex items-start gap-4">
            <div className="mt-1 flex-shrink-0">
              <div className="rounded-full bg-purple-100 p-2">
                {success ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex items-center gap-2">
                <Zap className="h-4 w-4 text-purple-600" />
                <div className="text-sm font-semibold text-gray-900">
                  {success ? 'Smart Fetch Complete' : 'Fetch Failed'}
                </div>
                {success && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${getMethodBadge(method)}`}
                  >
                    {method}
                  </span>
                )}
              </div>

              <div className="mb-3 break-all text-xs text-gray-600">
                <span className="font-medium">{url}</span>
              </div>

              {error && !success && (
                <div className="mb-3 rounded-2xl bg-red-50 p-2 text-xs text-red-600">{error}</div>
              )}

              <div className="mb-3 flex items-center gap-3 text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  <span>{getDomain(url)}</span>
                </div>
                {statusCode > 0 && (
                  <div className={`font-medium ${getStatusColor(statusCode)}`}>{statusCode}</div>
                )}
                {platform && <span className="text-gray-500">• {platform}</span>}
              </div>

              {title && (
                <div className="mb-3">
                  <div className="mb-1 text-xs font-medium text-gray-700">Title:</div>
                  <div className="rounded-2xl bg-gray-50 p-2 text-xs text-gray-800">
                    {title.length > 100 ? `${title.slice(0, 100)}...` : title}
                  </div>
                </div>
              )}

              {description && description !== title && (
                <div className="mb-3">
                  <div className="mb-1 text-xs font-medium text-gray-700">Description:</div>
                  <div className="rounded-2xl bg-gray-50 p-2 text-xs text-gray-800">
                    {description.length > 200 ? `${description.slice(0, 200)}...` : description}
                  </div>
                </div>
              )}

              {author && (
                <div className="mb-3">
                  <div className="mb-1 text-xs font-medium text-gray-700">Author:</div>
                  <div className="text-xs text-gray-600">{author}</div>
                </div>
              )}

              {success && response.text && (
                <div className="mb-3">
                  <div className="mb-1 text-xs font-medium text-gray-700">Content:</div>
                  <div className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-2xl bg-gray-50 p-3 text-xs text-gray-800">
                    {response.text.length > 5000
                      ? `${response.text.slice(0, 5000)}...`
                      : response.text}
                  </div>
                </div>
              )}

              {success && response.content && method === 'web' && (
                <CollapsibleHtmlSection html={response.content} />
              )}

              {instagramPost && (
                <div className="mt-4">
                  <InstagramPostItem post={instagramPost} />
                </div>
              )}

              <div className="border-t border-gray-200 pt-2">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
                >
                  <ExternalLink className="h-3 w-3" />
                  View Original
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadFetchFromUrl(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function fetchFromUrlToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(fetchFromUrl, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const url = inputs.url || ''
  const responses = Array.isArray(result) ? result : [result]
  const response = responses[0]
  const success = response?.success !== false && !response?.error
  const error = response?.error || ''
  const method = response?.method || 'web'
  const title = response?.metadata?.title || ''
  const description = response?.metadata?.description || ''
  const author = response?.metadata?.author || ''
  const platform = response?.metadata?.platform || ''
  const statusCode = response?.status || 0

  return `## Smart URL Fetch

**Status:** ${success ? '✅ Success' : '❌ Failed'}
**Method:** ${method}
**URL:** ${url}
${platform ? `**Platform:** ${platform}` : ''}
${statusCode > 0 ? `**Status Code:** ${statusCode}` : ''}
${title ? `**Title:** ${title}` : ''}
${description ? `**Description:** ${description}` : ''}
${author ? `**Author:** ${author}` : ''}
${error ? `**Error:** ${error}` : ''}

---
`
}

export const FetchFromUrlDisplay = createWorkflowToolComponent(
  FetchFromUrlDisplayComponent,
  downloadFetchFromUrl,
  fetchFromUrlToMarkdown
)
